<script setup lang="ts">
import { Badge as ApiBadge } from '@/api/model';
import { useLayout } from '@/layout/composables/layout';
import { i18n, t } from '@/utils/i18n';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

const { layoutState, setActiveMenuItem, toggleMenu } = useLayout();
const isMenuCollapsed = computed(() => layoutState.menuCollapsed);

const props = defineProps({
    item: {
        type: Object,
        default: () => ({})
    },
    index: {
        type: Number,
        default: 0
    },
    root: {
        type: Boolean,
        default: true
    },
    parentItemKey: {
        type: String,
        default: null
    }
});

const badge = computed<ApiBadge | null>(() => props.item.badge as ApiBadge);

const isActiveMenu = ref(false);
const itemKey = ref(null);

onBeforeMount(() => {
    itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);

    const activeItem = layoutState.activeMenuItem;

    isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(itemKey.value + '-') : false;
});

watch(
    () => layoutState.activeMenuItem,
    (newVal) => {
        isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
    }
);

function itemClick(event, item) {
    if (item.disabled) {
        event.preventDefault();
        return;
    }

    if ((item.to || item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
        toggleMenu();
    }

    if (item.command) {
        item.command({ originalEvent: event, item: item });
    }

    const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey) : itemKey.value;

    setActiveMenuItem(foundItemKey);
}

function checkActiveRoute(item) {
    const itemToUrl = router.resolve(item.to).path + "/";
    let url = route.query["returnTo"];

    if (url === null || url === undefined) {
        url = route.path;
    }

    url = url + "/";

    return url.startsWith(itemToUrl);
}
</script>

<template>
    <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
        <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">{{ t(item.label) }}</div>
        <router-link v-if="item.to && !item.items && item.visible !== false" @click="itemClick($event, item)" :class="[item.class, { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="item.to" class="menu-item-container">
            <div>
                <i v-if="!isMenuCollapsed" 
                    :class="item.icon" 
                    class="layout-menuitem-icon"></i>
                <i v-if="isMenuCollapsed" 
                    :class="item.icon" 
                    class="layout-menuitem-icon" 
                    v-tooltip="t(item.label)"></i>
                <span class="layout-menuitem-text" v-show="!isMenuCollapsed">{{ t(item.label) }}</span>
            </div>
            <Badge v-if="!isMenuCollapsed && badge" :value="badge.text" :severity="badge.hervorgehoben ? 'warn' : ''" class="menuitem-badge" v-tooltip="badge.tooltip[i18n.global.locale.value]" />
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items && !isMenuCollapsed"></i>
        </router-link>
        <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
            <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
                <app-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parentItemKey="itemKey" :root="false"></app-menu-item>
            </ul>
        </Transition>
    </li>
</template>
